import { AudioRingBuffer } from './ring_buffer';
import { SttAdapter } from './stt_adapter';
import { EvidenceItem } from '../core/align/types';
import { normalizeForMatching } from '../core/text/normalizer';

export interface SchedulerStats {
  readonly inFlight: boolean;
  readonly lastLatencyMs: number;
  readonly measuredRtf: number;
  readonly droppedStaleCount: number;
  readonly totalDecodes: number;
  readonly provisionalDisabled: boolean;
}

export interface DecodeSchedulerConfig {
  defaultHopMs?: number;
  defaultWindowSec?: number;
  maxSegmentSec?: number;
  endOfUtteranceSilenceMs?: number;
}

export class DecodeScheduler {
  private ringBuffer: AudioRingBuffer;
  private adapter: SttAdapter;

  private inFlight = false;
  private currentSeq = 0;
  private droppedStaleCount = 0;
  private totalDecodes = 0;
  private lastLatencyMs = 0;
  private measuredRtf = 0.2; // initial conservative estimate
  private provisionalDisabled = false;

  private lastDecodeTimeMs = 0;
  private lastProvisionalText = '';

  private readonly defaultHopMs: number;
  private readonly defaultWindowSec: number;
  private readonly maxSegmentSec: number;
  private readonly endOfUtteranceSilenceMs: number;

  private onEvidenceCallback: ((item: EvidenceItem) => void) | null = null;
  private onStatsCallback: ((stats: SchedulerStats) => void) | null = null;

  constructor(
    ringBuffer: AudioRingBuffer,
    adapter: SttAdapter,
    config: DecodeSchedulerConfig = {}
  ) {
    this.ringBuffer = ringBuffer;
    this.adapter = adapter;
    this.defaultHopMs = config.defaultHopMs ?? 600;
    this.defaultWindowSec = config.defaultWindowSec ?? 4.0;
    this.maxSegmentSec = config.maxSegmentSec ?? 15.0;
    this.endOfUtteranceSilenceMs = config.endOfUtteranceSilenceMs ?? 500;
  }

  onEvidence(cb: (item: EvidenceItem) => void): void {
    this.onEvidenceCallback = cb;
  }

  onStats(cb: (stats: SchedulerStats) => void): void {
    this.onStatsCallback = cb;
  }

  getStats(): SchedulerStats {
    return {
      inFlight: this.inFlight,
      lastLatencyMs: this.lastLatencyMs,
      measuredRtf: this.measuredRtf,
      droppedStaleCount: this.droppedStaleCount,
      totalDecodes: this.totalDecodes,
      provisionalDisabled: this.provisionalDisabled,
    };
  }

  private notifyStats(): void {
    if (this.onStatsCallback) {
      this.onStatsCallback(this.getStats());
    }
  }

  /**
   * Called every VAD cycle with current speech state
   */
  tick(
    isSpeechActive: boolean,
    speechDurationMs: number,
    silenceDurationMs: number,
    nowMs: number
  ): void {
    // 1. Check if decode is already in flight (INV-6: strictly at most 1 decode in flight)
    if (this.inFlight) {
      return;
    }

    // 2. Trigger FINAL decode on end of utterance or max segment reached
    const isEndOfUtterance = !isSpeechActive && silenceDurationMs >= this.endOfUtteranceSilenceMs && speechDurationMs > 400;
    const isMaxSegmentReached = isSpeechActive && speechDurationMs >= this.maxSegmentSec * 1000;

    if (isEndOfUtterance || isMaxSegmentReached) {
      const windowSec = Math.min(this.maxSegmentSec, Math.max(1.5, speechDurationMs / 1000.0));
      this.triggerDecode('final', windowSec, nowMs);
      return;
    }

    // 3. Trigger PROVISIONAL decode while speech is active
    if (isSpeechActive && !this.provisionalDisabled) {
      const elapsedSinceLastDecode = nowMs - this.lastDecodeTimeMs;
      // Adaptive hop: if RTF is high, lengthen hop
      const currentHopMs = Math.max(this.defaultHopMs, this.lastLatencyMs * 1.5);

      if (elapsedSinceLastDecode >= currentHopMs && speechDurationMs >= 800) {
        this.triggerDecode('provisional', this.defaultWindowSec, nowMs);
      }
    }
  }

  private async triggerDecode(
    kind: 'provisional' | 'final',
    windowSec: number,
    nowMs: number
  ): Promise<void> {
    if (this.inFlight) return;

    this.inFlight = true;
    this.lastDecodeTimeMs = nowMs;
    const seq = ++this.currentSeq;
    this.notifyStats();

    const audio = this.ringBuffer.extractWindow(windowSec);
    const audioDurationSec = audio.length / 16000.0;

    if (audioDurationSec < 0.4) {
      this.inFlight = false;
      this.notifyStats();
      return;
    }

    const startTime = performance.now();

    try {
      const res = await this.adapter.transcribe(audio);
      const latencyMs = performance.now() - startTime;
      this.lastLatencyMs = latencyMs;
      this.totalDecodes++;

      // Update RTF estimate: latency / audioDuration
      const currentRtf = (latencyMs / 1000.0) / audioDurationSec;
      this.measuredRtf = this.measuredRtf * 0.7 + currentRtf * 0.3;

      // If device is too slow (RTF > 0.8), disable provisional decodes to relieve main thread/worker
      if (this.measuredRtf > 0.8) {
        this.provisionalDisabled = true;
      } else if (this.measuredRtf < 0.5) {
        this.provisionalDisabled = false;
      }

      // Check for staleness: if a newer sequence superseded this one, drop it
      if (seq !== this.currentSeq) {
        this.droppedStaleCount++;
        return;
      }

      // Hallucination Guards (§11.5)
      const rawText = res.text.trim();
      if (!rawText) return;

      // Skip reprocessing unchanged provisional text
      if (kind === 'provisional' && rawText === this.lastProvisionalText) {
        return;
      }
      if (kind === 'provisional') {
        this.lastProvisionalText = rawText;
      } else {
        this.lastProvisionalText = '';
      }

      // Tokenize normalized observation
      const rawTokens = rawText.split(/\s+/).filter((s) => s.length > 0);
      const obsTokens = rawTokens
        .map((t) => normalizeForMatching(t))
        .filter((t) => t.length > 0);

      // Guard: excessive tokens relative to audio duration (e.g. > 5 words per second)
      if (obsTokens.length > audioDurationSec * 6.0) {
        // Hallucinated repetition loop, drop
        return;
      }

      if (obsTokens.length > 0 && this.onEvidenceCallback) {
        this.onEvidenceCallback({
          seq,
          kind,
          audioEndTime: nowMs,
          rawText,
          obsTokens,
        });
      }
    } catch (err) {
      console.warn('[DecodeScheduler] Transcribe error:', err);
    } finally {
      this.inFlight = false;
      this.notifyStats();
    }
  }

  reset(): void {
    this.inFlight = false;
    this.currentSeq++;
    this.lastProvisionalText = '';
    this.adapter.cancel();
  }
}
